import { useQuery } from '@tanstack/react-query';
import { List, Popover, Space, Tooltip, Typography } from 'antd';
import dayjs from 'dayjs';
import PropTypes from 'prop-types';
import CSSGrid from '@/common/components/CSSGrid';
import { queryMainAgentEntries } from '../services/chat';

const getRecordTitle = (record) => record?.title || record?.keyword || record?.id || '未命名入口';

const renderText = (value) => value || '-';

const renderDate = (value) => (value ? dayjs(value).format('YYYY-MM-DD') : '-');

const AgentEntryList = ({
  dataSource,
  loading,
  renderContent,
  renderExtra,
  renderMetaDescription,
  title,
}) => (
  <List
    header={<Typography.Text strong>{title}</Typography.Text>}
    bordered
    dataSource={dataSource}
    loading={loading}
    pagination={false}
    renderItem={(item) => (
      <List.Item actions={[renderExtra(item)]}>
        <List.Item.Meta
          title={
            <Popover
              content={renderText(item.description)}
              placement="topLeft"
              styles={{ root: { width: 360 } }}
            >
              <Typography.Text ellipsis={1}>{getRecordTitle(item)}</Typography.Text>
            </Popover>
          }
          description={renderMetaDescription ? renderMetaDescription(item) : null}
        />
        {renderContent(item)}
      </List.Item>
    )}
    rowKey="id"
    size="small"
  />
);

AgentEntryList.propTypes = {
  dataSource: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.string })),
  loading: PropTypes.bool,
  renderContent: PropTypes.func.isRequired,
  renderExtra: PropTypes.func.isRequired,
  renderMetaDescription: PropTypes.func,
  title: PropTypes.string.isRequired,
};

AgentEntryList.defaultProps = {
  dataSource: [],
  loading: false,
  renderMetaDescription: undefined,
};

const AgentEntryPanel = ({ onEntryStart, organizationId }) => {
  const { data, isLoading } = useQuery({
    enabled: Boolean(organizationId),
    queryFn: () => queryMainAgentEntries({ organizationId }),
    queryKey: ['agents-chat', 'main-agent-entries', organizationId],
  });

  const renderRunAction = (seedType, record) => (
    <Tooltip title="选择并开始创作">
      <Typography.Link
        href="#"
        onClick={(event) => {
          event.preventDefault();
          onEntryStart({
            record,
            seedId: record.id,
            seedTitle: getRecordTitle(record),
            seedType,
          });
        }}
      >
        选择
      </Typography.Link>
    </Tooltip>
  );
  const renderSeedPlanExtra = (record) => renderRunAction('seedPlan', record);
  const renderHotPotExtra = (record) => renderRunAction('hotPot', record);
  const renderSeedPlanContent = (record) => (
    <Typography.Text type="secondary">{renderDate(record.plannedAt)}</Typography.Text>
  );
  const renderHotPotContent = (record) => (
    <Typography.Text type="secondary">{renderDate(record.createdAt)}</Typography.Text>
  );
  const renderHotPotDescription = (record) => (
    <Space size="large" wrap>
      <Typography.Text type="secondary">指数：{renderText(record.hot)}</Typography.Text>
      <Typography.Text type="secondary">契合度：{renderText(record.relevance)}</Typography.Text>
    </Space>
  );

  return (
    <CSSGrid columns={1} gap="large">
      <AgentEntryList
        dataSource={data?.seedPlans || []}
        loading={isLoading}
        renderContent={renderSeedPlanContent}
        renderExtra={renderSeedPlanExtra}
        title="近期规划"
      />
      <AgentEntryList
        dataSource={data?.hotPots || []}
        loading={isLoading}
        renderContent={renderHotPotContent}
        renderExtra={renderHotPotExtra}
        renderMetaDescription={renderHotPotDescription}
        title="最新热点"
      />
    </CSSGrid>
  );
};

AgentEntryPanel.propTypes = {
  onEntryStart: PropTypes.func.isRequired,
  organizationId: PropTypes.string,
};

AgentEntryPanel.defaultProps = {
  organizationId: undefined,
};

export default AgentEntryPanel;
